import React, { useState } from 'react'
import { Experience } from '@/types'
import PortableText from '@/components/ui/PortableText'
import { SlideIn } from '@/components/animations/SlideIn'
import { FadeIn } from '@/components/animations/FadeIn'

interface ExperienceTimelineProps {
  experiences: Experience[]
}

const ExperienceTimeline: React.FC<ExperienceTimelineProps> = ({ experiences }) => {
  const [filter, setFilter] = useState<'all' | 'blockchain'>('all')

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
    })
  }

  const formatDateRange = (startDate: string, endDate: string | null) => {
    const start = formatDate(startDate)
    const end = endDate ? formatDate(endDate) : 'Present'
    return `${start} - ${end}`
  }

  const filteredExperiences =
    filter === 'all' ? experiences : experiences.filter(exp => exp.isBlockchainRelated)

  return (
    <div className="space-y-6">
      {/* Filter buttons */}
      <FadeIn>
        <div className="flex justify-center mb-8">
          <div className="inline-flex rounded-md shadow-sm bg-white dark:bg-gray-800 p-1">
            <button
              onClick={() => setFilter('all')}
              className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                filter === 'all'
                  ? 'bg-primary text-white'
                  : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
            >
              All Experiences
            </button>
            <button
              onClick={() => setFilter('blockchain')}
              className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                filter === 'blockchain'
                  ? 'bg-primary text-white'
                  : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
            >
              Blockchain Only
            </button>
          </div>
        </div>
      </FadeIn>

      <div className="relative">
        {/* Timeline line */}
        <div className="absolute left-4 md:left-8 top-0 bottom-0 w-0.5 bg-gradient-to-b from-primary to-secondary"></div>

        <div className="space-y-8">
          {filteredExperiences.length > 0 ? (
            filteredExperiences.map((experience, index) => (
              <SlideIn key={experience._id} delay={index * 0.1}>
                <div className="relative flex items-start">
                  {/* Timeline dot */}
                  <div
                    className={`absolute left-2 md:left-6 w-4 h-4 rounded-full border-4 border-white dark:border-gray-900 shadow-lg z-10 ${
                      experience.isBlockchainRelated ? 'bg-secondary animate-pulse' : 'bg-primary'
                    }`}
                  ></div>

                  {/* Content */}
                  <div className="ml-12 md:ml-20 flex-1">
                    <div
                      className={`bg-white dark:bg-gray-800 rounded-lg p-6 shadow-lg border hover:shadow-xl transition-all duration-300 ${
                        experience.isBlockchainRelated
                          ? 'border-secondary dark:border-secondary/30'
                          : 'border-gray-200 dark:border-gray-700'
                      }`}
                    >
                      {experience.isBlockchainRelated && (
                        <div className="flex justify-end mb-2">
                          <span className="px-3 py-1 bg-secondary/20 text-secondary dark:text-secondary-light rounded-full text-xs font-medium">
                            Blockchain
                          </span>
                        </div>
                      )}

                      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-4">
                        <div>
                          <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-1">
                            {experience.role}
                          </h3>
                          <h4 className="text-lg font-semibold text-primary-light dark:text-primary-light">
                            {experience.company}
                          </h4>
                          {experience.location && (
                            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                              {experience.location}
                            </p>
                          )}
                        </div>
                        <div className="text-sm text-gray-600 dark:text-gray-400 mt-2 md:mt-0">
                          {formatDateRange(experience.startDate, experience.endDate)}
                        </div>
                      </div>

                      {/* Description */}
                      <div className="prose prose-sm dark:prose-invert max-w-none mb-4">
                        <PortableText content={experience.description} />
                      </div>

                      {/* Technologies */}
                      {experience.technologies && experience.technologies.length > 0 && (
                        <div className="mb-4">
                          <h5 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">
                            Technologies Used:
                          </h5>
                          <div className="flex flex-wrap gap-2">
                            {experience.technologies.map(tech => (
                              <span
                                key={tech._id}
                                className={`px-3 py-1 rounded-full text-xs font-medium ${
                                  tech.category === 'blockchain'
                                    ? 'bg-secondary/20 text-secondary dark:text-secondary-light'
                                    : 'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200'
                                }`}
                              >
                                {tech.name}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Achievements */}
                      {experience.achievements && experience.achievements.length > 0 && (
                        <div>
                          <h5 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">
                            Key Achievements:
                          </h5>
                          <ul className="list-disc list-inside space-y-1">
                            {experience.achievements.map((achievement, idx) => (
                              <li key={idx} className="text-sm text-gray-600 dark:text-gray-400">
                                {achievement}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </SlideIn>
            ))
          ) : (
            <div className="text-center py-12 ml-12 md:ml-20">
              <p className="text-gray-500 dark:text-gray-400">
                No experiences match the selected filter.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default ExperienceTimeline
